// The create form, rendered in the same drawer the records open in.
//
// A drawer rather than a page on purpose: creating a treaty is something you
// do while looking at the treaty register, and the list you are adding to
// stays visible behind it. It also means one panel component, one set of
// edges, and Escape already closes it.
//
// Deliberately NOT a full workflow. These forms carry the fields a record
// needs to exist and be recognised — the rest is edited on the record
// afterwards. A create form that demands everything is a create form nobody
// finishes.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BASE_URL, C, Drawer, PrimaryButton, GhostButton,
} from './kit';

const label = {
  display: 'block', fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5,
};
const control = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13,
  color: C.ink, background: '#fff', fontFamily: 'inherit',
};

// A select whose options are REAL RECORDS, fetched from the API, submitting
// the chosen record's id.
//
// Reference fields used to be typed as free text — "Expiring treaty:
// TRY-2026-000004" — and submitted as a label the schema does not accept,
// which is why creating a renewal always failed on `treaty` being required.
// Picking from the list cannot produce an id that does not exist, and the
// user never has to know a reference by heart.
function LookupField({ field, common }) {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BASE_URL}${field.endpoint}`, { params: { limit: 200, ...(field.query || {}) }, withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data?.data || []);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [field.endpoint, field.query]);

  const optionLabel = field.optionLabel || ((r) => r.name || r._id);
  return (
    <>
      <select {...common} disabled={state !== 'ready' || rows.length === 0}>
        <option value=''>
          {state === 'loading' ? 'Loading…'
            : state === 'error' ? 'Could not load the list'
              : rows.length === 0 ? (field.emptyLabel || 'Nothing to choose from yet')
                : (field.placeholder || 'Select…')}
        </option>
        {rows.map(r => (
          <option key={r._id} value={r._id}>{optionLabel(r)}</option>
        ))}
      </select>
      {state === 'ready' && rows.length === 0 && field.emptyHelp && (
        <div style={{ fontSize: 11.5, color: C.faint, marginTop: 4 }}>{field.emptyHelp}</div>
      )}
    </>
  );
}

function Field({ field, value, error, onChange }) {
  const id = `f-${field.key}`;
  const common = {
    id,
    value: value ?? '',
    onChange: (e) => onChange(field.key, e.target.value),
    style: { ...control, borderColor: error ? C.red : C.border },
    'aria-invalid': error ? 'true' : undefined,
    'aria-describedby': error ? `${id}-err` : undefined,
  };

  return (
    <div>
      <label htmlFor={id} style={label}>
        {field.label}
        {field.required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
      </label>

      {field.type === 'lookup' ? (
        <LookupField field={field} common={common} />
      ) : field.type === 'select' ? (
        <select {...common}>
          <option value=''>{field.placeholder || 'Select…'}</option>
          {(field.options || []).map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : field.type === 'textarea' ? (
        <textarea {...common} rows={3} placeholder={field.placeholder} />
      ) : (
        <input
          {...common}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          placeholder={field.placeholder}
          {...(field.type === 'number' ? { min: 0, step: 'any' } : {})}
        />
      )}

      {error && (
        <div id={`${id}-err`} style={{ fontSize: 11.5, color: C.red, marginTop: 4 }}>{error}</div>
      )}
      {!error && field.help && (
        <div style={{ fontSize: 11.5, color: C.faint, marginTop: 4 }}>{field.help}</div>
      )}
    </div>
  );
}

export default function CreateForm({ open, config, onClose, onCreated }) {
  const initial = useMemo(() => {
    const out = {};
    for (const f of config?.fields || []) out[f.key] = f.defaultValue ?? '';
    return out;
  }, [config]);

  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState('');
  // Remount the form when the drawer is reopened, so a cancelled draft does
  // not reappear in the next one.
  const [nonce, setNonce] = useState(0);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  React.useEffect(() => {
    if (open) {
      setValues(initial);
      setErrors({});
      setFailure('');
      setConfirmDiscard(false);
      setNonce(n => n + 1);
    }
  }, [open, initial]);

  if (!open || !config) return null;

  const set = (key, v) => {
    setValues(prev => ({ ...prev, [key]: v }));
    // Clear the error as soon as the field is touched: leaving it under a
    // field the user is actively fixing reads as though it is still wrong.
    setErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const submit = async () => {
    const found = {};
    for (const f of config.fields) {
      const v = values[f.key];
      if (f.required && (v === '' || v === null || v === undefined)) {
        found[f.key] = `${f.label} is required`;
      }
    }
    // Cross-field rules the server also enforces, checked here so the user
    // is told before a round trip rather than after one.
    if (config.validate) Object.assign(found, config.validate(values) || {});

    const real = Object.fromEntries(Object.entries(found).filter(([, v]) => v));
    if (Object.keys(real).length) { setErrors(real); return; }

    setSaving(true);
    setFailure('');
    try {
      const payload = config.toPayload ? config.toPayload(values) : values;
      const res = await axios.post(`${BASE_URL}${config.endpoint}`, payload, { withCredentials: true });
      // SOME ENDPOINTS CREATE A SET, NOT A RECORD.
      //
      // /accounts/generate opens one technical account per participating
      // market and returns them ALL; /accounts/from-closing returns its one
      // account inside an array too. `data` is then an ARRAY, and handing that
      // to the drawer as though it were a record is what crashed the page:
      //
      //   TypeError: (i.entries || []).filter is not a function
      //
      // because on an array `entries` is Array.prototype.entries — a FUNCTION,
      // so it is truthy and every `(r.entries || [])` guard in the drawer fails
      // open. The error boundary then replaced the whole screen, which is what
      // QA reported as "shows something went wrong page" after a successful
      // creation. The records were created; only the render died.
      //
      // Exactly one row opens the drawer on it. A set of several refreshes the
      // list behind the form and opens nothing — picking one of them to show
      // would be arbitrary.
      const body = res.data?.data;
      const created = Array.isArray(body)
        ? (body.length === 1 ? body[0] : null)
        : (body || null);
      onCreated(created);
    } catch (e) {
      const status = e?.response?.status;
      setFailure(
        status === 403
          ? (e?.response?.data?.message || 'You do not have permission to create this.')
          : (e?.response?.data?.message || e.message || 'Could not save. Nothing was created.'),
      );
    } finally {
      setSaving(false);
    }
  };

  // Anything typed yet? A form with content must not be thrown away by a
  // stray Escape — which is exactly what closing a native select does.
  const dirty = config.fields.some((f) => {
    const now = values[f.key];
    const then = f.defaultValue ?? '';
    return String(now ?? '') !== String(then);
  });

  const tryClose = () => {
    if (!dirty) { onClose(); return; }
    setConfirmDiscard(true);
  };

  return (
    <Drawer
      open={open}
      onClose={tryClose}
      eyebrowTitle={config.title}
      lines={config.subtitle ? [config.subtitle] : []}
    >
      <div key={nonce} style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
        {confirmDiscard && (
          <div style={{
            border: `1px solid ${C.border}`, background: C.panel, borderRadius: 8,
            padding: '11px 12px', display: 'grid', gap: 9,
          }}>
            <div style={{ fontSize: 12.5, color: C.body, lineHeight: 1.5 }}>
              Discard this {config.title.toLowerCase()}? What you have typed will be lost.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <GhostButton onClick={() => { setConfirmDiscard(false); onClose(); }}>Discard</GhostButton>
              <PrimaryButton icon={false} onClick={() => setConfirmDiscard(false)}>Keep editing</PrimaryButton>
            </div>
          </div>
        )}
        {failure && (
          <div style={{
            border: `1px solid ${C.border}`, background: '#fff7f7', color: C.red,
            borderRadius: 8, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.5,
          }}>
            {failure}
          </div>
        )}

        {config.fields.map(f => (
          <Field key={f.key} field={f} value={values[f.key]} error={errors[f.key]} onChange={set} />
        ))}

        <div style={{
          display: 'flex', gap: 9, marginTop: 6, paddingTop: 14,
          borderTop: `1px solid ${C.hair}`,
        }}>
          <PrimaryButton icon={false} onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : (config.submitLabel || 'Create')}
          </PrimaryButton>
          <GhostButton onClick={tryClose} disabled={saving}>Cancel</GhostButton>
        </div>

        <p style={{ margin: 0, fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
          {config.footnote
            || 'Only the essentials here — everything else is edited on the record once it exists.'}
        </p>
      </div>
    </Drawer>
  );
}
